<template>
  <el-select
    filterable
    reserve-keyword
    default-first-option
    :infinite-scroll="infiniteScrollHandler"
    @change="handleChange"
    :remote-method="getRemoteMethod"
    v-bind="$attrs"
    v-on="$listeners"
    ref="select"
  >
    <template slot="prefix" v-if="$slots.prefix">
      <slot name="prefix"></slot>
    </template>
    <template slot="empty" v-if="$slots.empty">
      <slot name="empty"></slot>
    </template>
    <slot>
      <el-option v-for="item in options" :key="item.key || item.value" :label="item | getLabel" :value="getValue(item)">
        <slot name="option" />
      </el-option>
    </slot>
  </el-select>
</template>

<script>
  import { getValueByPath } from 'element-ui/lib/utils/util'
  export default {
    name: 'MgSelect',
    props: {
      optionKey: String,
      data: {
        type: Array,
        default: () => [],
      },
      // 通过接口加载数据
      loadMethod: Function,
      loadParams: Object,
      loadInit: {
        type: Boolean,
        default: true,
      },
      /**
       * 当传入loadMethod时，需要设置字段的映射关系
       * Function: 回调函数，与数组的map方法回调使用一致
       * Object: {label: 'xxxName', code: 'xxxCode'}
       */
      map: {
        required: false,
        type: [Function, Object],
      },
      resultProp: {
        type: [Function, String],
        default: 'data',
      },
      // 是否启用无限滚动
      infiniteScrollEnabled: Boolean,
      // 远程搜索使用的关键字
      searchKey: {
        type: String,
        default: 'searchKey',
      },
      // 默认项，仅用于启用远程搜索时使用
      defaultItems: {
        type: Array,
        default: () => [],
      },
      itemChange: Function,
    },
    data() {
      return {
        page: this?.loadParams?.page || 1,
        size: this?.loadParams?.size || 20,
        loadOptions: null,
        scrollLoading: false,
        cachedOptions: [],
      }
    },
    computed: {
      options() {
        // 以 this.loadOptions 数据为主，添加默认项用于回显
        const options = this.loadOptions || this.data
        this.defaultItems.forEach((item) => {
          const index = options.findIndex((n) => n[this.optionKey] === item[this.optionKey])
          if (index === -1) {
            options.unshift(item)
          }
        })
        return this.mapOptions(options)
      },
      infiniteScrollHandler() {
        return this.infiniteScrollEnabled ? this.handleInfiniteScroll : null
      },
      getRemoteMethod() {
        return this.$attrs.remote ? this.remoteMethod : null
      },
    },
    filters: {
      getLabel(item) {
        if (item.code) {
          return item.code + '-' + item.label
        }
        return item.label
      },
    },
    mounted() {
      if (typeof this.loadMethod === 'function' && this.loadInit) {
        this.initLoadData()
      }
    },
    beforeDestroy() {
      this.cachedOptions = []
    },
    methods: {
      getValue(item) {
        if (this.$attrs['value-key']) return item
        return item.value
      },
      focus() {
        this.$refs.select.focus()
      },
      initLoadData(params) {
        this.loadData(params).then((data) => {
          this.loadOptions = data
        })
      },
      remoteMethod(query) {
        this.page = 1
        this.query = query
        const queryParams = {
          [this.searchKey]: query,
        }
        if (!this.defaultItems?.length) {
          queryParams.supplierCodesExclude = []
          queryParams.customerCodesExclude = []
        }
        this.initLoadData(queryParams)
      },
      loadData(params) {
        this.scrollLoading = true
        if (this.$attrs.remote) {
          params = { ...params, page: this.page, size: this.size }
        }
        return this.loadMethod({ ...this.loadParams, ...params })
          .then((res) => {
            this.page++
            return typeof this.resultProp === 'function' ? this.resultProp(res) : getValueByPath(res, this.resultProp)
          })
          .finally(() => {
            this.scrollLoading = false
          })
      },
      mapOptions(data = []) {
        if (!this.map) return data
        let map = this.map
        if (typeof map !== 'function') {
          map = (item) => {
            const res = {}
            for (const key in this.map) {
              res[key] = item[this.map[key]]
            }
            return res
          }
        }
        return data.map(map)
      },
      handleInfiniteScroll() {
        if (this.scrollLoading) return
        const params = {}
        if (this.$attrs.remote && this.query !== void 0) {
          params[this.searchKey] = this.query
        }
        this.loadData(params).then((data) => {
          if (data?.length) {
            this.loadOptions.push(...data)
          }
        })
      },
      handleChange(value) {
        if (this.$attrs['value-key']) {
          value = value.map((item) => item[this.$attrs['value-key']])
        }
        // 远程分页 + 多选
        if (this.$attrs.remote && this.$attrs.multiple) {
          const items = this.options.filter((item) => value.includes(item.value))
          items.forEach((item) => {
            if (!this.cachedOptions.find((n) => n.value === item.value)) {
              this.cachedOptions.push(item)
            }
          })
          this.cachedOptions = this.cachedOptions.filter((item) => value.includes(item.value))
          this.$emit('item-change', this.cachedOptions)
          return
        }

        // 不分页多选
        if (this.$attrs.multiple) {
          const items = this.options.filter((item) => value.includes(item.value))
          this.$emit('item-change', items)
          return
        }
        // 单选
        const item = this.options.find((item) => item.value === value)
        this.$emit('item-change', item)
      },
    },
  }
</script>
